function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback } from 'react';
import clsx from 'clsx';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'common/i18n/hooks';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { DATASET_MIXPANEL, FeatureGate, ReportName } from 'common/report/constants';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { InputReact } from 'common/components/input';
import { InputSize, InputType } from 'common/components/input/types';
import { QueryBuilderReact } from 'common/query-builder/query-builder';
import { Directions } from 'common/components/drop-menu/types';
import { getEntries, getShowSection } from 'common/report/insights/util/events';
import { QueryBuilderUpdateDetail } from 'common/query-builder/query-builder/types';
import { FiltersOperator, isFormulaEntry, isMetricEntry, MetricEntry, QueryFilterEntry, QueryMetricEntry, QueryMultiMetricEntry, RowLabelTheme, RowLabelType, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { getMetricEntryBehavior, isSavedBehavior } from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';
import { getProjectId, getWorkspaceId } from 'common/report/util';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { INSIGHTS_DEFAULT_METRIC } from 'common/report/insights/models/report';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { RollingWindowSize } from 'common/util';
import { getResolvedFormulaEntry } from 'common/util/formulas';
import { KEY_VALUES } from 'common/util/dom';
import { getFilterSection, getPropertyFilters } from 'common/report/insights/util/filters';
import { FilterClause } from 'common/report/insights/models/filter-clause';
import { FilterClause as FilterClauseType } from 'common/report/insights/util/types';
import { transformToGlobalQueryFilterEntries } from 'common/query-builder/query-builder/util';
import { MetricDefinition, MetricsResponse } from 'common/react-query/metrics/types';
import { IncludeCustomPropertyType } from 'common/query-builder/query-block/properties-menu/types';
import { MetricType } from 'common/types/reports/bookmark';
import { hydrateSavedBehaviors, isBehaviorShowClause, isFormulaShowClause, isSavedMetricShowClause } from 'common/report/insights/models/utils';
import { BookmarkType } from 'common/report/queries/app-api/bookmark-client';
import { getOtherAggregationOptions, getOtherFilterOptions } from 'common/report/insights/query-builder/utils';
import { ShowClause } from 'common/report/insights/models/show-clause';
import { CurrentProject } from 'common/report/model-resources/projects';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { getActiveBoardFilters } from 'apps/boards/src/dashboards-app/util';
import { TimeUnit } from 'common/widgets/date-range-picker/models/types';
import { insightsFilterAttrsFromPropertyFilter } from 'common/widgets/property-filter-menu/models/insights-filter';
import { useCanvasStore } from '../store';
import { buildMetricRequest, getMetricNodeTitle } from '../nodes/metric-node/utils';
import { MetricNodeType } from '../nodes/metric-node/types';
import { metricsNodeQueryKeyPrefix } from '../react-query';
import styles from './index.module.styl';
export function QueryBuilderPanel(_ref: {
  activeMetricNode: MetricNodeType;
}) {
  var _useCurrentProject$ge, _useCurrentProject$ge2, _userCookieUtils$getU, _activeMetricNode$dat, _activeMetricNode$dat2, _activeMetricNode$dat3, _activeMetricNode$dat4, _metricResponse$meta, _metricResponse$meta2, _activeMetricNode$dat5;
  let {
    activeMetricNode
  }: {
    activeMetricNode: MetricNodeType;
  } = _ref;
  const isViewOnly = useCanvasStore(store => {
    var _store$canvas;
    return !((_store$canvas = store.canvas) !== null && _store$canvas !== void 0 && _store$canvas.can_update_basic);
  });
  const globalFilters = useCanvasStore(store => store.globalFilters);
  const asOfDate = useCanvasStore(store => store.asOfDate);
  const globalTimeRangesAndComparisons = useCanvasStore(store => store.timeRangesAndComparisons);
  const selectedTimeRangeIndex = useCanvasStore(store => {
    var _store$activeTimeRang;
    return (_store$activeTimeRang = store.activeTimeRangeIndex[activeMetricNode.id]) !== null && _store$activeTimeRang !== void 0 ? _store$activeTimeRang : 0;
  });
  const setActiveMetricNodeId = useCanvasStore(store => store.setActiveMetricNodeId);
  const setMetricNodeDefinition = useCanvasStore(store => store.setMetricNodeDefinition);
  const setMetricDataGroupId = useCanvasStore(store => store.setMetricDataGroupId);
  const setMetricFilters = useCanvasStore(store => store.setMetricFilters);
  const setMetricNodeName = useCanvasStore(store => store.setMetricNodeName);
  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const currentProject: CurrentProject | null = (_useCurrentProject$ge = (_useCurrentProject$ge2 = useCurrentProject().get()) === null || _useCurrentProject$ge2 === void 0 ? void 0 : _useCurrentProject$ge2.value) !== null && _useCurrentProject$ge !== void 0 ? _useCurrentProject$ge : null;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const getQueriesData = useCallback((prefix: QueryKey) => {
    var _ref2, _queryClient$getQueri;
    return (_ref2 = ((_queryClient$getQueri = queryClient.getQueriesData({
      queryKey: prefix
    })) === null || _queryClient$getQueri === void 0 || (_queryClient$getQueri = _queryClient$getQueri[0]) === null || _queryClient$getQueri === void 0 ? void 0 : _queryClient$getQueri[1]) as MetricsResponse) !== null && _ref2 !== void 0 ? _ref2 : null;
  }, [queryClient]);
  const maxHistoryDays = (currentProject === null || currentProject === void 0 ? void 0 : currentProject.getFeatureGate(FeatureGate.MaxDataHistoryDays)) || 365 * 5;
  const projectId = Number(getProjectId());
  const workspaceId = Number(getWorkspaceId());
  const userId = (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id;
  const additionalFilters = getActiveBoardFilters(globalFilters).map(propertyFilter => {
    return insightsFilterAttrsFromPropertyFilter(_objectSpread({
      dataset: DATASET_MIXPANEL
    }, propertyFilter));
  });
  const dataGroupId = (_activeMetricNode$dat = activeMetricNode.data) === null || _activeMetricNode$dat === void 0 ? void 0 : _activeMetricNode$dat.metricDataGroupId;
  let metricDefinition: MetricDefinition = ((_activeMetricNode$dat2 = activeMetricNode.data) === null || _activeMetricNode$dat2 === void 0 ? void 0 : _activeMetricNode$dat2.metricDefinition) || null;
  // hydrate metric definition with saved behaviors
  metricDefinition = metricDefinition ? hydrateSavedBehaviors(metricDefinition, behaviors) : metricDefinition;
  const metricNodeTitle = getMetricNodeTitle({
    metricName: ((_activeMetricNode$dat3 = activeMetricNode.data) === null || _activeMetricNode$dat3 === void 0 ? void 0 : _activeMetricNode$dat3.metricName) || "",
    metricDefinition,
    metrics
  });
  const timeRangesAndComparisons = ((_activeMetricNode$dat4 = activeMetricNode.data) === null || _activeMetricNode$dat4 === void 0 ? void 0 : _activeMetricNode$dat4.timeRangesAndComparisons) || globalTimeRangesAndComparisons;
  const dateRange = timeRangesAndComparisons[selectedTimeRangeIndex].timeRange;
  const timeComparison = timeRangesAndComparisons[selectedTimeRangeIndex].timeComparison;
  const metricRequest = buildMetricRequest({
    nodeData: activeMetricNode.data,
    asOfDate,
    dateRange: new DateRange(dateRange),
    timeComparison,
    additionalFilters,
    projectId: projectId.toString()!,
    workspaceId: workspaceId.toString()
  });
  const metricResponse = metricDefinition ? getQueriesData(metricsNodeQueryKeyPrefix(metricRequest)) : null;
  let currentEntry: null | QueryMetricEntry | MetricEntry = metricDefinition ? getEntries(_objectSpread({
    showClauses: [metricDefinition],
    behaviorsResource: behaviors,
    dateUnit: TimeUnit.Day
  }, (metricResponse === null || metricResponse === void 0 || (_metricResponse$meta = metricResponse.meta) === null || _metricResponse$meta === void 0 ? void 0 : _metricResponse$meta.retention_buckets_size) !== undefined ? {
    reportSectionsMetadata: {
      show: [{
        retention_buckets_size: metricResponse === null || metricResponse === void 0 || (_metricResponse$meta2 = metricResponse.meta) === null || _metricResponse$meta2 === void 0 ? void 0 : _metricResponse$meta2.retention_buckets_size
      }]
    }
  } : {}))[0] as QueryMetricEntry | MetricEntry : null;
  if (currentEntry && isViewOnly) {
    // hydrate entry if it's a saved formula entry or a metric entry with a saved behavior
    if (isFormulaEntry(currentEntry)) {
      const resolvedFormulaEntry = getResolvedFormulaEntry({
        metricsResource: metrics,
        behaviorsResource: behaviors,
        formulaEntry: currentEntry
      });
      currentEntry = resolvedFormulaEntry ? _objectSpread(_objectSpread({}, resolvedFormulaEntry), {}, {
        isExpanded: true
      }) : currentEntry;
    } else if (isMetricEntry(currentEntry) && isSavedBehavior(currentEntry)) {
      const savedBehavior = currentEntry.behavior.id && behaviors.getOne(currentEntry.behavior.id).value;
      const metricBehavior = savedBehavior ? getMetricEntryBehavior(_objectSpread({
        id: savedBehavior.id,
        name: savedBehavior.name
      }, savedBehavior.definition.behavior)) : null;
      currentEntry = _objectSpread(_objectSpread({}, currentEntry), metricBehavior ? {
        behavior: metricBehavior,
        isExpanded: true
      } : {});
    }
  }
  const metricFilters: FilterClauseType[] = (_activeMetricNode$dat5 = activeMetricNode.data) === null || _activeMetricNode$dat5 === void 0 ? void 0 : _activeMetricNode$dat5.metricFilters;
  const currentFilterEntries: QueryFilterEntry[] = metricFilters ? transformToGlobalQueryFilterEntries(getPropertyFilters(metricFilters.map(filter => new FilterClause(filter)))) : [];
  let metricType: MetricType | null = null;
  if (metricDefinition) {
    if (isSavedMetricShowClause(metricDefinition)) {
      metricType = MetricType.SavedMetric;
    } else if (isFormulaShowClause(metricDefinition)) {
      metricType = MetricType.Formula;
    } else if (isBehaviorShowClause(metricDefinition)) {
      var _metricDefinition$beh;
      metricType = ((_metricDefinition$beh = metricDefinition.behavior) === null || _metricDefinition$beh === void 0 ? void 0 : _metricDefinition$beh.type) || null;
    }
  }
  const handleMetricNameUpdate = (updatedMetricName: string) => {
    if (activeMetricNode.id) {
      setMetricNodeName(activeMetricNode.id, updatedMetricName);
    }
  };
  const handleQueryBuilderUpdate = (updates: QueryBuilderUpdateDetail) => {
    var _delta$filters, _delta$dataGroupId;
    const delta = updates.delta;
    const hasUpdatedMetricDefinition = !!delta.entries;
    const hasUpdatedMetricFilters = !!delta.filters;
    const hasUpdatedDataGroupId = delta.dataGroupId !== undefined;
    const updatedMetricDefinition: MetricDefinition = getShowSection({
      entries: delta.entries ? delta.entries as Array<QueryMetricEntry | MetricEntry> : []
    }).toBookmarkData()[0] || null;
    const updatedMetricFilters: FilterClauseType[] = getFilterSection((_delta$filters = delta.filters) !== null && _delta$filters !== void 0 ? _delta$filters : [], FiltersOperator.And).toBookmarkData();
    const updatedDataGroupId = (_delta$dataGroupId = delta.dataGroupId) !== null && _delta$dataGroupId !== void 0 ? _delta$dataGroupId : "";
    if (hasUpdatedMetricDefinition) setMetricNodeDefinition(activeMetricNode.id, updatedMetricDefinition);
    if (hasUpdatedMetricFilters) setMetricFilters(activeMetricNode.id, updatedMetricFilters);
    if (hasUpdatedDataGroupId) setMetricDataGroupId(activeMetricNode.id, updatedDataGroupId);
  };
  const handleClose = () => {
    setActiveMetricNodeId(null);
  };
  return <div className={clsx(styles.qbPanel, {
    [styles.disabled]: isViewOnly
  })} data-sentry-component="QueryBuilderPanel" data-sentry-source-file="qb-panel.tsx">
      <div className={clsx(styles.qbHeader, {
      [styles.empty]: !metricNodeTitle
    })}>
        <SvgIconReact icon='data-metric-outlined' data-sentry-element="SvgIconReact" data-sentry-source-file="qb-panel.tsx" />
        <InputReact className={styles.metricNameInput} readonly={isViewOnly} type={InputType.Text} size={InputSize.Medium} placeholder='Metric name' value={metricNodeTitle} onBlur={ev => handleMetricNameUpdate(ev.target.value as string)} onKeydown={ev => {
        if (ev.key === KEY_VALUES.ENTER) {
          ev.preventDefault();
          (ev.target as HTMLInputElement).blur();
        }
      }} data-sentry-element="InputReact" data-sentry-source-file="qb-panel.tsx" />
        <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={_ => handleClose()} data-sentry-element="ButtonReact" data-sentry-source-file="qb-panel.tsx" />
      </div>
      <QueryBuilderReact
    // set the key to force re-render a QB when the active metric node changes
    key={activeMetricNode.id} className={clsx(styles.qbContent, {
      [styles.disabled]: isViewOnly
    })} queryBuilderContextValue={{
      isCoreReportQb: false,
      qbAlignment: Directions.Right
    }} addEntryButtonTooltipText={t("query-builder.add-metric")} allDataGroups={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} dataGroupsEnabled={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} canCustomizeNumericBuckets dataGroupId={dataGroupId} dateRange={dateRange} dateUnit={RollingWindowSize.Day} disableAddEntry={!!currentEntry} disableCloneEntry enableHideEntry={false} disableAddEntryReason={currentEntry ? "Only one metric can be connected to a canvas card at a time" : ""} disableBreakdown
    // TODO[CC] - resolve saved metrics and check if one of the referenced metrics has people
    disableProfileHistory={metricType === MetricType.People} enableEnhancedListFilters
    // TODO[CC] - resolve saved metrics and check if one of the referenced metrics has funnel
    enableFilterAttribution={metricType === MetricType.Funnel} entryRowLabelType={RowLabelType.Alpha} entryRowLabelTheme={RowLabelTheme.Gray} entries={currentEntry ? [currentEntry] : []} emptyPlaceholder={INSIGHTS_DEFAULT_METRIC as QueryMultiMetricEntry} filters={currentFilterEntries} hideDatePicker includeCustomProperties={IncludeCustomPropertyType.All} maxDataHistoryDays={maxHistoryDays} metricsMode otherFilterOptions={getOtherFilterOptions({
      showClauses: metricDefinition ? [ShowClause.createShowClause(metricDefinition)] : [],
      hasGlobalDataGroup: false,
      insightsReportMode: BookmarkType.Insights,
      multiAttributionProperty: null,
      currentProject
    })}
    // TODO[CC] - resolve saved metrics and check if one of the referenced metrics has event
    otherAggregationOptions={getOtherAggregationOptions({
      hasEventsQuery: metricType === MetricType.Event,
      insightsReportMode: BookmarkType.Insights
    })} projectAsOfDate={(currentProject === null || currentProject === void 0 ? void 0 : currentProject.projectAsOfDate) || undefined} projectHasCohorts={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseCohorts()} projectHasPeople={currentProject === null || currentProject === void 0 ? void 0 : currentProject.hasIntegratedEngage} projectIsFreePlan={currentProject === null || currentProject === void 0 ? void 0 : currentProject.isFreeFeatureGateTier()} queryOrigin={"canvas"} readonlyEntries={isViewOnly} reportName={ReportName.Insights} restrictTopEventsOnly={false} rowTypes={[SelectorTypeDefinitions.Metrics]} showBorrowedProperties showEntryRename showSessions showTopEvents showSpecialDatasetProperty={currentProject === null || currentProject === void 0 ? void 0 : currentProject.isRollupProject()} stagingRow={!isViewOnly} supportB2bCohorts={currentProject === null || currentProject === void 0 ? void 0 : currentProject.canUseDataGroups()} projectId={projectId} userId={userId} workspaceId={workspaceId} onUpdate={handleQueryBuilderUpdate} data-sentry-element="QueryBuilderReact" data-sentry-source-file="qb-panel.tsx" />
    </div>;
}